import cx from 'classnames';
import {isString, truncate} from 'lodash';
import {useCallback, useMemo} from 'react';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import {useTranslation} from 'react-i18next';

import {Icon, IconColor, notifier, Tooltip, IconSize} from 'components';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.COMMON;

type Props = {
    text: string;
    label?: React.ReactNode;
    truncateLength?: number;
    labelPlacement?: 'left' | 'right';
    iconOnly?: boolean;
    iconColor?: IconColor;
    iconSize?: IconSize;
    disabled?: boolean;
    copiedLabelOnly?: boolean;
    textInTooltip?: boolean;
    className?: string;
    children?: React.ReactNode;
};

export const CopyText: React.FC<Props> = ({
    text, textInTooltip = true, label, iconOnly, copiedLabelOnly, className, iconColor, iconSize, children, disabled,
    labelPlacement = 'right', truncateLength
}: Props) => {
    const {t} = useTranslation();

    const onClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
    }, []);

    const onCopy = useCallback(() => {
        if (!disabled) {
            notifier.info({title: copiedLabelOnly ? t(T_KEYS.ALL.COPIED) : `${t(T_KEYS.ALL.COPIED)} ${text}`});
        }
    }, [disabled, copiedLabelOnly, text, t]);

    const labelText = useMemo(() => {
        const result = label || text;
        if (!truncateLength) {
            return result;
        } else if (isString(result)) {
            return truncate(result, {length: truncateLength});
        } else {
            return result;
        }
    }, [label, text, truncateLength]);

    return (
        <div className={cx('vs-copy-text tw-inline-flex', className)} onClick={onClick}>
            <Tooltip disabled={disabled || !!children} message={`${t(T_KEYS.BUTTONS.COPY)}${textInTooltip ? ` ${text}` : ''}`}>
                <CopyToClipboard
                    text={text}
                    onCopy={onCopy}
                >
                    {children || (
                        <span className='tw-inline-flex tw-items-center tw-cursor-pointer'>
                            {!iconOnly &&
                                <span className={cx(`tw-inline-block ${labelPlacement === 'right' ? 'tw-ml-1' : 'tw-mr-1'}`, {
                                    'tw-order-2': labelPlacement === 'right'
                                })}
                                >
                                    {labelText}
                                </span>}
                            <Icon name='copy' button color={iconColor} size={iconSize} />
                        </span>
                    )}
                </CopyToClipboard>
            </Tooltip>
        </div>
    );
};
